import React from 'react'
import View from 'views/View'
import { Box } from '@mui/material'
import {
    MerchantSwitcher,
    useActiveMerchant,
} from 'components'
import idDirectory from './idAttributes'
import { IDateRange } from 'components/PresetDateRange'
import { withRequiredRole } from 'components/useRequireRole'
import { format } from 'date-fns'
import EntFeature from 'models/EntFeature'
import { dateUtilities } from 'utils/dateUtilities'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import useReportsCharts from './Charts/useReportsCharts'
import { ReportsChartVariant } from '../../../shared'
import ReportsFilters from './sections/ReportsFilters'


export interface IReportFilter {
    merchant_id: number
    mid_id: number
    currency_id: number
    date_reference: number
    date_range: string
    gateway_id: number
    platforms: number
}

export const ReportsView: React.FC = () => {
    const testId = 'reports'
    const { CHARTS = false } = useFeatureToggle('REPORTS')

    const reportsChart = CHARTS
    const {
        enabled: isReportsEnabled = false,
        charts_and_position = {},
        default_date_range = {},
    } = reportsChart ?? false

    const { id: clientId } = useActiveMerchant()
    const defaultDateRangeOnLoad: {
        to: Date
        from: Date
    } = dateUtilities.getPriorDays(30)
    const { to, from } = defaultDateRangeOnLoad
    const presetDateRangeDate: IDateRange = {
        to: format(to, 'yyyy-MM-dd'),
        from: format(from, 'yyyy-MM-dd'),
    }
    const initialApiDateRange = `${presetDateRangeDate.from}|${presetDateRangeDate.to}`

    const [filterValues, setFilterValues] = React.useState<IReportFilter>({
        merchant_id: +clientId,
        mid_id: -1,
        currency_id: -1,
        date_reference: 0,
        date_range: initialApiDateRange,
        gateway_id: 0,
        platforms: 0,
    })

    const { chartMap, chartSectionKeys } = useReportsCharts({
        filterValues,
        default_date_range,
    })

    React.useEffect(() => {
        setFilterValues({
            ...filterValues,
            merchant_id: +clientId,
        })
    }, [clientId]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <View
            title="Reports"
            breadcrumb="Reports"
            HeaderInterior={
                <div className="emp-viewHeaderContainer">
                    <ReportsFilters
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        presetDateRange={presetDateRangeDate}
                    />
                    <MerchantSwitcher
                        testId="reports"
                        className="emp-merchantSwitcher"
                    />
                </div>
            }
            testId={testId}
        >
            {isReportsEnabled ? (
                <div id={idDirectory.divGrid} className={'emp-grid'}>
                    {chartSectionKeys.map((chartSection: string) => {
                        return (
                            <div
                                id={`${idDirectory.divSection}-${chartSection}`}
                                className={'emp-chartSection'}
                                key={`key-row-${chartSection}`}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: '16px',
                                        gridTemplateRows: 'auto',
                                        gridTemplateColumns: '1fr',
                                        '@media (min-width:600px)': {
                                            gridTemplateColumns:
                                                'repeat(1, 1fr)',
                                            gap: '16px',
                                        },
                                        '@media (min-width:1175px)': {
                                            gridTemplateColumns: `repeat(${charts_and_position[chartSection].charts.length}, 1fr)`,
                                            gap: '16px',
                                        },
                                    }}
                                >
                                    {charts_and_position[
                                        chartSection
                                    ].charts.map(
                                        (
                                            chartVariant: ReportsChartVariant
                                        ): React.ReactElement =>
                                            chartMap[chartVariant]
                                    )}
                                </Box>
                            </div>
                        )
                    })}
                </div>
            ) : null}
        </View>
    )
}

export default withRequiredRole(EntFeature.REPORTS, ReportsView)
